import { Component, effect, EventEmitter, Input, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { DOCUMENTATION_URL, DOMAIN_TOKEN, PER_PAGE } from '@microsec/constants';
import { SCAN_TYPES } from '../shared-active-scan-form.component';
import { ConnectionService, TargetDeviceService } from '@ids-services';
import { CardComponent, CommonTableComponent, TruncateTextComponent } from '@microsec/components';
import { LazyLoadEvent } from 'primeng/api';
import { finalize, switchMap } from 'rxjs';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { ArrayMapPipe, ConstantPipe } from '@ids-pipes';

@Component({
  selector: 'app-active-scan-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TooltipModule,
    ButtonModule,
    RadioButtonModule,
    CardComponent,
    CommonTableComponent,
    TruncateTextComponent,
    ArrayMapPipe,
    ConstantPipe,
  ],
  providers: [ArrayMapPipe, ConstantPipe],
})
export class ConfirmationComponent extends BaseComponent implements OnInit {
  isLoading = false;

  @Input() scanType: WritableSignal<string> = signal('');

  edgeDevices: any[] = [];

  connections: any[] = [];

  currentPage = 1;

  totalRecords = 0;

  PER_PAGE = PER_PAGE;

  @ViewChild('dt') dt!: CommonTableComponent;

  _selectedEdgeDevices: any[] = [];

  get selectedEdgeDevices() {
    return this._selectedEdgeDevices;
  }

  @Input() set selectedEdgeDevices(value: any[]) {
    this._selectedEdgeDevices = value;
    this.selectedEdgeDevicesChange.emit(value);
  }

  @Output() selectedEdgeDevicesChange: EventEmitter<any> = new EventEmitter<any>();

  cols: any[] = [
    { field: 'label', header: 'Name', width: 15 },
    { field: 'connections', header: 'Connections', width: 15 },
    { field: 'type', header: 'Type', width: 15 },
    { field: 'zones', header: 'Zones', width: 15 },
  ];

  @Output() cancelEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() confirmEvent: EventEmitter<any> = new EventEmitter<any>();

  SCAN_TYPES = SCAN_TYPES;

  constructor(
    private targetDeviceSrv: TargetDeviceService,
    private connectionSrv: ConnectionService,
  ) {
    super();
    effect(() => {
      if (this.scanType() === SCAN_TYPES.EDGE) {
        this.getEdgeDevices();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.prepareConfigs();
  }

  async getEdgeDevices(resetSelectedDevices = true, event?: LazyLoadEvent) {
    await this.prepareConfigs();
    this.isLoading = true;
    const page = !event ? this.currentPage : Math.floor((event as any)?.first / (event?.rows as number)) + 1;
    const perPage = event?.rows || this.dt?.datatable?.rows || PER_PAGE;
    this.connectionSrv
      .getConnections()
      .pipe(
        switchMap((connectionData: any) => {
          this.connections = (connectionData?.data as any[]) || [];
          return this.targetDeviceSrv.getDevices({
            organizationId: this.breadcrumbConfig?.organizationId,
            projectId: this.breadcrumbConfig?.projectId,
            page,
            perPage,
            usesAgent: true,
            detailed: false,
          });
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          this.currentPage = res?.page;
          this.totalRecords = res?.total_record;
          this.edgeDevices = ((res?.devices as any[]) || []).map((device) => {
            device.connections = ((device.connection_ids as any[]) || []).map((id) => this.connections.find((p) => p.id === id));
            return device;
          });
          if (!!resetSelectedDevices) {
            this.selectedEdgeDevices = [];
          }
        },
        error: (error) => {
          this.showErrorMessage(error);
          this.selectedEdgeDevices = [];
        },
      });
  }

  onCancel() {
    this.cancelEvent?.emit();
  }

  onNext() {
    this.confirmEvent?.emit();
  }

  goToDocumentation() {
    const customDomain: any = localStorage.getItem('domain');
    window.open(DOCUMENTATION_URL.replace(DOMAIN_TOKEN, customDomain), '_blank');
  }
}
